import { useEffect, useRef, useState } from 'react'
import { useEditor } from '../store/useEditor'
import { useAuth } from '../store/useAuth'
import {
  IconChevronLeft,
  IconExport,
  IconMore,
  IconPhoto,
  IconPresent,
  IconRedo,
  IconRuler,
  IconSend,
  IconSparkMark,
  IconUndo,
} from './icons'
import { BRAND } from '../lib/brand'

function savedLabel(state: string, at: number): { text: string; tone: string } {
  if (state === 'saving') return { text: 'Saving', tone: 'busy' }
  if (state === 'error') return { text: 'Not saved', tone: 'bad' }
  if (state === 'saved') {
    const secs = Math.max(0, Math.round((Date.now() - at) / 1000))
    if (secs < 5) return { text: 'Saved just now', tone: 'ok' }
    if (secs < 60) return { text: `Saved ${secs}s ago`, tone: 'ok' }
    const mins = Math.round(secs / 60)
    return { text: `Saved ${mins}m ago`, tone: 'ok' }
  }
  return { text: '', tone: 'idle' }
}

export default function TopBar({
  onProjects,
  onNew,
  onHome,
  onClearRuns,
  hasRuns,
  onFit,
  panelOpen,
  onTogglePanel,
  onShare,
  canShare,
  exporting,
  onQuote,
  onShareDesign,
  onMeasure,
  onChangePhoto,
}: {
  /** The mark is a door: press it and you are back at the job list. */
  onHome: () => void
  onClearRuns: () => void
  hasRuns: boolean
  onFit: () => void
  onProjects: () => void
  onNew: () => void
  panelOpen: boolean
  onTogglePanel: () => void
  onShare: () => void
  canShare: boolean
  exporting: boolean
  onQuote: () => void
  /** The one thing most people open this app to do. See ui/ShareSheet.tsx. */
  onShareDesign: () => void
  /** What anybody knows about the house. See ui/MeasureSheet.tsx. */
  onMeasure: () => void
  /*
   * SWAPPING THE PHOTOGRAPH IS RARE AND IT USED TO COST A WHOLE HEADER ROW.
   * A second bar under the top bar carried the job name again, a count of
   * what was drawn, and this control. Three facts, one of them repeated, to
   * reach one button somebody presses when they took a better picture. It
   * lives in the More menu now and the row is gone.
   */
  onChangePhoto: () => void
}) {
  const doc = useEditor((s) => s.doc)
  const past = useEditor((s) => s.past)
  const future = useEditor((s) => s.future)
  const saveState = useEditor((s) => s.saveState)
  const savedAt = useEditor((s) => s.savedAt)
  const undo = useEditor((s) => s.undo)
  const redo = useEditor((s) => s.redo)
  const setDocName = useEditor((s) => s.setDocName)
  const email = useAuth((st) => st.session?.user.email ?? '')
  const signOut = useAuth((st) => st.signOut)
  const setPresent = useEditor((s) => s.setPresent)
  const advanced = useEditor((s) => s.advanced)
  const setAdvanced = useEditor((s) => s.setAdvanced)

  const [menu, setMenu] = useState(false)
  const [, tick] = useState(0)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Keeps "Saved 20s ago" honest without a render on every frame.
  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 10000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!menu) return
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menu])

  const saved = savedLabel(saveState, savedAt)

  return (
    <header className="topbar">
      <div className="tb-left">
        {/*
          THE REAL MARK, AS ARTWORK.

          It was a two letter badge and the words beside it, set in the app's
          own UI font - a stand-in, and it read as one. This is the actual
          logo file, so the star, the letterforms and the spacing are the
          brand's rather than an approximation of it.

          Alt is empty and the title carries the name: the logo is decorative
          here, and a screen reader announcing "LightsOS" twice, once for
          the image and once for the tooltip, is worse than once.
        */}
        {/*
          THE MARK GOES HOME, because in every other product it does.

          It was decoration - a div with a picture in it - and the one thing a
          person reaches for when they want out of a screen is the logo in the
          corner. Reported by the owner exactly that way: clicking that should
          always take you to the main dashboard page.

          Home means the job list, not a blank editor. Nothing is lost on the
          way: the job in front of you saved itself as you drew it and is the
          first row on the list you land on.
        */}
        {/*
          THE WAY OUT COMES FIRST, AND IT IS A WORD.

          The mark alone was the only way back to the list, which every other
          product also does and which nobody finds on the first try: a picture
          in a corner is not a labelled exit. Jobs sits at the left with a back
          chevron, in front of the mark, so leaving is a thing you read rather
          than a thing you guess.
        */}
        <button className="tb-back" onClick={onProjects} title="Every job you have saved">
          <IconChevronLeft size={16} />
          Jobs
        </button>

        {/*
          THE MARK IS A TILE, NOT A WORDMARK.

          The reference frames draw it as a small black rounded square with
          the spark in it, sitting between Jobs and the job's name. A 96px
          wordmark in the same place was reading as the product's masthead on
          a screen whose subject is somebody's house, and it was the widest
          thing on the left of the bar for no work done.

          It keeps its job: press it and the photograph drops, which is what
          puts the job list back on screen.
        */}
        <button
          className="logo tb-mark"
          onClick={onHome}
          aria-label={`${BRAND}, back to your jobs`}
          title={`${BRAND} - back to your jobs`}
        >
          <IconSparkMark size={15} />
        </button>

        <input
          className="proj-name"
          value={doc.name}
          onChange={(e) => setDocName(e.target.value)}
          aria-label="Project name"
          spellCheck={false}
        />

                {/*
          SAVED JUST NOW BELONGS BESIDE THE NAME IT IS ABOUT.

          It sat in the middle of the bar between the undo arrows, where it
          read as a status of the app rather than of this job, and where it
          pushed the arrows off centre every time the wording changed length.
        */}
        {saved.text && (
          <span className={`save-status ${saved.tone}`}>
            <i />
            {saved.text}
          </span>
        )}
      </div>

      <div className="tb-right">
        <button className="icon-btn" onClick={undo} disabled={!past.length} title="Undo">
          <IconUndo />
        </button>
        <button className="icon-btn" onClick={redo} disabled={!future.length} title="Redo">
          <IconRedo />
        </button>
        {/*
          EXPORT IS UP HERE; PRESENT IS IN THE MENU.

          The bar along the bottom is for the things you do TO the photograph -
          find the runs, draw one, make it evening - and it wants to be small
          enough to keep out of the way of the house. Export is the last thing
          you do, once, and it is not editing anything, so it belongs with the
          job's name and its save state rather than under a thumb.

          Present was here and it is also the third tile inside Export, where
          it reads as Full screen. Two buttons for one action in the two
          furthest corners of the screen is worse than either alone, so the
          duplicate went and the tile stayed.
        */}
        {/*
          ONE ACTION, AND IT IS THE ONE PEOPLE CAME FOR.

          Quote and Export used to sit here as two equal buttons, which made
          the common case - text the homeowner the picture from the driveway -
          one of two choices rather than the point of the screen. Share design
          renders the image and shows it; downloading it, sending it and
          pricing the house all live under it, where somebody looking at the
          finished picture is already looking. See ui/ShareSheet.tsx.

          Quote is still reachable in one press from that sheet, and still
          reachable from the menu here for the person who wants the paperwork
          without looking at the picture again.
        */}
        {/*
          MEASURING IS A JOB-LEVEL ACT, NOT A DRAWING TOOL.

          It sits up here with Jobs and Share design rather than on the bar
          along the bottom, which is three buttons on purpose - the argument
          is at the top of Dock.tsx and it is still right. Secondary weight,
          because most sessions are still somebody drawing a picture; but on
          screen at all times, because a bid that goes out on a guessed length
          is the expensive mistake this whole phase exists to prevent.
        */}
        {/*
          NEW JOB STAYS ON THE BAR, AND THE REFERENCE DOES NOT HAVE IT.

          The design package's top bar drops it and its More menu does not
          carry it either, so following the reference exactly would delete the
          control outright. It is on this bar because of an incident: it was a
          menu item called Start over, and the person who had just built a
          whole season asked how to start a new house. There is a test standing
          over that decision, and it caught this deletion.

          Jobs then New design is two presses and a screen change away, which
          is the path that failed the first time. So it keeps the reference's
          treatment, a ghost link in ink-2 beside Measure, and keeps existing.
          Raised with the design chat.
        */}
        {/*
          AND ON THE REFERENCE'S BAR IT IS AN ICON, BECAUSE THE BAR HAS ROOM
          FOR FIVE THINGS AND THIS IS THE SIXTH.

          The frames carry undo, redo, Measure, Share design and the menu. New
          job as a sixth labelled control is what made the right hand side
          read as a toolbar. It keeps a title and a real target size, so it is
          still one press from where somebody who has just finished a house is
          looking, which is the whole reason the incident above ended here.
        */}
        {doc.photo && (
          <button
            className="icon-btn"
            onClick={onNew}
            title="This job is saved. Start a new one."
            aria-label="Start a new job"
          >
            <IconPhoto size={17} />
          </button>
        )}

        <button
          className="btn"
          onClick={onMeasure}
          disabled={!doc.photo}
          title="Sections, lengths, and what has actually been measured"
        >
          <IconRuler size={16} />
          <span>Measure</span>
        </button>

        <button
          className="btn primary"
          onClick={onShareDesign}
          disabled={exporting || !doc.photo}
          title="See the finished picture, then send it or price it"
        >
          <IconSend size={16} />
          <span>{exporting ? 'Rendering' : 'Share design'}</span>
        </button>

        <div className="menu-wrap" ref={menuRef}>
          <button
            className="icon-btn"
            onClick={() => setMenu((v) => !v)}
            title="More"
            aria-expanded={menu}
          >
            <IconMore />
          </button>
          {menu && (
            <div className="menu" role="menu">
              {/*
                Only Advanced has a panel to hide. In simple mode the inspector
                comes and goes with the selection, so an item promising to hide
                a panel would be a control that does nothing.
              */}
              {advanced && (
                <button
                  onClick={() => {
                    onTogglePanel()
                    setMenu(false)
                  }}
                >
                  <span>{panelOpen ? 'Hide panel' : 'Show panel'}</span>
                </button>
              )}
              <button
                disabled={!doc.photo}
                onClick={() => {
                  onQuote()
                  setMenu(false)
                }}
              >
                <span>Price this job</span>
              </button>
              <button
                disabled={!doc.photo}
                onClick={() => {
                  onChangePhoto()
                  setMenu(false)
                }}
              >
                <IconPhoto size={14} />
                <span>Change photo</span>
              </button>
              {/*
                EXPAND PHOTO IS A VIEWING UTILITY, not a step. It hides the
                chrome so a homeowner can look at their own house on the
                tablet you are holding out to them.
              */}
              <button
                disabled={!doc.photo}
                onClick={() => {
                  setPresent(true)
                  setMenu(false)
                }}
              >
                <IconPresent size={14} />
                <span>Expand photo</span>
              </button>
              {canShare && (
                <button
                  disabled={exporting || !doc.photo}
                  onClick={() => {
                    onShare()
                    setMenu(false)
                  }}
                >
                  <span>Send picture</span>
                </button>
              )}
              <button
                onClick={() => {
                  onFit()
                  setMenu(false)
                }}
              >
                <span>Fit to view</span>
              </button>
              <div className="menu-sep" />
              {/*
                Jobs and New job are on the bar now, so they are not repeated
                here. Two doors to the same room is how somebody ends up sure
                there are two rooms.

                What is left is the one thing people meant by "start over" that
                the bar does not cover: taking the lights off the house in
                front of you and keeping the photograph.
              */}
              <button
                disabled={!hasRuns}
                onClick={() => {
                  onClearRuns()
                  setMenu(false)
                }}
              >
                <span>Remove all lights</span>
              </button>
              <div className="menu-sep" />
              {/*
                ADVANCED, BURIED, WHICH IS WHERE IT BELONGS.

                It used to be a button at the bottom of the panel a contractor
                looked at all day. It is the workshop - every control for every
                category at once, and the developer's night grade panel - and
                nobody standing in a driveway wants it. One item in a menu is
                findable by the person who needs it and invisible to everyone
                else.
              */}
              <button
                onClick={() => {
                  setAdvanced(!advanced)
                  setMenu(false)
                }}
              >
                <span>{advanced ? 'Simple view' : 'Advanced'}</span>
              </button>

              {/*
                WHOSE ACCOUNT THIS IS, AND HOW TO LEAVE IT.
                
                There was no way to sign out at all, and nothing anywhere said
                which account you were in. A contractor who signs in with the
                wrong address is stuck, and two people sharing a laptop in a
                shop have no way to swap - which is the ordinary case in this
                trade, not the edge one.
                
                It belongs at the bottom of the menu rather than on the bar:
                needed once, and never in the middle of quoting a house. The
                address is shown rather than a generic Account, because the
                whole question somebody has is which one am I in.
              */}
              {email && (
                <>
                  <div className="menu-sep" />
                  <p className="menu-who" title={email}>
                    {email}
                  </p>
                  <button onClick={() => void signOut()}>
                    <span>Sign out</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
